import { fn } from '@common/utils';
import { CompatibilityService } from '@common/services/compatibilityservice';
import { ISetting } from '@common/services/settingsservice';
import { IKWFRoute } from '../../KWFMain';
import { $ModalRequestClose, IDialogRoute } from '@common/services/DialogService';

export interface IPersistedCustomMenuItem {
    Title: string;
    Hint?: string;
    Link?: string;
    Items?: ICustomMenuItem[];
}

interface ITreeModel<TKey = any> {
    id?: TKey;
    parentId?: TKey;
    hasChildren?: boolean;
}
type ICustomMenuItem = IPersistedCustomMenuItem & ITreeModel<string>;

/** Reduce Properties of item by template per prop */
function reduceProps<TItem>(item: TItem, keepTemplate: Record<keyof TItem, boolean>) {
    Object.getOwnPropertyNames(item).forEach(name => {
        if (!keepTemplate[name] || !item[name]) {
            delete item[name];
        }
    });
    return item;
}
type TreeMenuItem = kendo.data.TreeListModel & ICustomMenuItem;

function fromStoredItems(item: ICustomMenuItem, parent?: ICustomMenuItem) {
    fn.ExtendObject(item, { id: item.id || fn.id(), parentId: (parent && parent.id) || null });
    item?.Items?.forEach(childItem => fromStoredItems(childItem, item));
    return item;
}

function toStoredItems(_items: ArrayLike<TreeMenuItem>) {
    var children: { [name: string]: ICustomMenuItem[] } = {};
    var items = fn.ToArray(_items || []).map(item => item.toJSON() as ICustomMenuItem);
    items.forEach(item => item && item.parentId && (children[item.parentId] || (children[item.parentId] = [])).push(item));
    items.forEach(item => item.Items = children[item.id] || undefined);
    var result = items.filter(item => !item.parentId);
    items.forEach(item => reduceProps(item, {
        Title: true, Hint: true, Items: true, Link: true,
        id: false, parentId: false, hasChildren: false
    }));
    return result;
}

export class CtrlCustomMenuItems {
    constructor(protected $c: CompatibilityService) {
        this.settings = $c.resolve("NavSettings").GetGlobalSetting<ICustomMenuItem>("KWF/CustomMenuItems");
    }
    private readonly settings: ISetting<ICustomMenuItem>;

    GetCurrentItems() {
        return this.$c.resolve("Rwf")("RwfBootstrap").Resolve.then(() =>
            this.settings.LoadValue().then(items => items && [fromStoredItems(items)],
                err => ([{ Title: 'Fehler!', Hint: fn.resolveToError(err) } as ICustomMenuItem])));
    }

    SaveCurrentItems(items: ICustomMenuItem) {
        if (items && (items.Link || items.Items?.length > 0)) {
            result = this.settings.SaveValue(items);
        } else {
            var result = this.settings.DeleteValue().then(fn.ToFunc(<ICustomMenuItem>undefined));
        }
        result.then(() => {
            // TODO: Trigger to refresh ...
        });

        return result;
    }
}

export class CtrlCustomMenuAdmin extends CtrlCustomMenuItems {
    static Template = require('./custommenu.ctrl.html');
    static Route: IKWFRoute | IDialogRoute = {
        name: 'CustomMenu',
        titleFormula: "'kundenspezifische Menueinträge anpassen'",
        template: CtrlCustomMenuAdmin.Template,
        controller: CtrlCustomMenuAdmin,
        controllerAs: 'vm'
    };

    static $inject = ["$scope", "CompatibilityService"];
    constructor($scope: ng.IScope, $c: CompatibilityService) {
        super($c);
        this.Refresh();

        $scope.$on($ModalRequestClose, (e, arg) => arg.Defer(() => this.Close()));
    }

    public TreeListOptions: kendo.ui.TreeListOptions;

    dataSource: kendo.data.TreeListDataSource;

    loadedItems: ICustomMenuItem[];

    public TreeList: kendo.ui.TreeList;

    isDirty: boolean;

    public Close() {
        const $c = this.$c;
        const dlg = $c.resolve("DialogService");
        return $c.when(this.isDirty &&
            $c.resolve("DialogService").ShowConfirm("Änderungen speichern?").then(answer =>
                answer && this.SaveCurrentItems(toStoredItems(this.dataSource.data())[0])).then(() => {
                    return dlg.ShowDialog("Geänderte Eintrage werden erst nach Neu Laden der Seite wirksam");
                }));
    }

    Refresh() {
        delete this.TreeListOptions;
        delete this.TreeList;
        const dataSource = this.dataSource = new kendo.data.TreeListDataSource({
            transport: {
                read: options => this.GetCurrentItems().then(items => options.success(this.loadedItems = items && fn.FlattenTree(items, "Items") || []),
                    err => options.error(fn.resolveToError(err))),
                update: options => {
                    this.isDirty = true;
                    return options.success(options.data);
                },
                create: options => {
                    var model = options.data;
                    model.id = fn.id();
                    this.isDirty = true;
                    options.success(model);
                },
                destroy: options => {
                    var model = options.data as TreeMenuItem;
                    this.isDirty = true;
                    dataSource.remove(model);
                    options.success(model);
                }
            },
            schema: {
                model: {
                    id: "id",
                    parentId: 'parentId',
                    expanded: true,
                    fields: {
                        Title: { validation: { required: true } },
                        Hint: { validation: { required: false } },
                        Link: {
                            validation: {
                                linkvalidation: function (input: JQuery) {
                                    if (input.is("[name='Link']")) {
                                        input.attr("data-linkvalidation-msg", "Nur 'Test' erlaubt");
                                        return true; // input.val() == "Test";
                                    }

                                    return true;
                                }
                            }
                        },
                    }
                },
            }
        });
        const linkTemplate = kendo.template("<a href='#= Link #' target='_blank' rel='noopener noreferrer' >#= Title #</a>");
        this.TreeListOptions = <kendo.ui.TreeListOptions>{
            editable: true,
            selectable: 'row',
            navigatable: true,
            toolbar: <kendo.ui.TreeListToolbarItem[]>[{
                name: "xcreate", imageClass: "k-i-add", text: "Neu", click: (e) => {
                    if (dataSource.data().length > 0) {
                        this.$c.resolve("DialogService").ShowError("Aktuell nur max. ein Element auf oberster Ebene zulässig");
                        return;
                    }
                    this.TreeList.addRow(undefined);
                }
            }],
            dataSource: dataSource,
            columns: [
                { field: "Title", title: "Titel", width: 200 },
                { field: "Link", title: "Ziel (URL)", width: 400, template: l => (l && l.Link && linkTemplate(l)) || '' },
                { field: "Hint", title: "Beschreibung", width: 400 },
                { title: "", command: [{ name: "edit", text: " " }, { name: "createchild", text: " " }, { name: "destroy", text: " " }], width: 280 }
            ],
            resizable: true
        };
    }
}
